/* eslint @typescript-eslint/strict-boolean-expressions: off */

import React from "react";
import widgetConfig from "./widgetConfig";
import { Layouts } from "../layouts";
import debounce from "lodash/debounce";
import { produce } from "immer";

type ResponsiveWidgets = {
  // widgetId: is the 'id' of the widget
  // ref: is the current widget ref
  [widgetId in string]: {
    ref: React.RefObject<HTMLElement>;
    minUnitHeight: number;
  };
};

type LayoutTransformer = (layouts: Layouts) => Layouts;

class ResponsiveHeights {
  private _responsiveWidgets: ResponsiveWidgets = {};

  constructor(public onResizeCallback: (fn: LayoutTransformer) => void) {}

  public addWidget(
    id: string,
    ref: React.RefObject<HTMLElement>,
    minUnitHeight: number
  ) {
    if (!this.hasResponsiveElements) {
      window.addEventListener("resize", this._onResize);
    }

    this._responsiveWidgets[id] = {
      ref,
      minUnitHeight,
    };

    this._onResize();
  }

  public destroy = () => {
    this._responsiveWidgets = {};
    window.removeEventListener("resize", this._onResize);
  };

  public getMinHeightLayout = produce((draft: Layouts) => {
    const activeBreakpoint = widgetConfig.getBreakpoint(window.innerWidth);

    Object.entries(this._responsiveWidgets).forEach(
      ([id, { minUnitHeight }]) => {
        const widgetIndex = draft[activeBreakpoint].findIndex(
          ({ i }) => id === i
        );

        if (widgetIndex >= 0) {
          draft[activeBreakpoint][widgetIndex].h = minUnitHeight;
        }
      }
    );
  });

  public getResponsiveHeightLayout = produce((draft: Layouts) => {
    const activeBreakpoint = widgetConfig.getBreakpoint(window.innerWidth);

    Object.entries(this._responsiveWidgets).forEach(
      ([id, { ref, minUnitHeight }]) => {
        const element = ref.current;
        const widgetIndex = draft[activeBreakpoint].findIndex(
          ({ i }) => id === i
        );

        // React grid layouts only supports integers for item heights.
        // As a result, when dynamically calculating the height of the cell in the grid we round up the height using Math.Ceil.
        // This produces white gaps between text widgets.
        // To overcome this we inherit the background colour of the text widget and apply it to the react grid cell DOM element.
        if (element && element.childNodes[0] && element.parentNode) {
          (element.parentNode as HTMLElement).style.background = window
            .getComputedStyle(element?.childNodes[0] as HTMLElement)
            .getPropertyValue("background");
        }

        if (widgetIndex >= 0 && element) {
          const { height: pixelHeight } = element.getBoundingClientRect();

          // unitHeight = (pixelHeight + margin)/(rowHeight+margin)
          draft[activeBreakpoint][widgetIndex].h = Math.max(
            Math.ceil(
              (pixelHeight + widgetConfig.margin) /
                (widgetConfig.rowHeight + widgetConfig.margin)
            ),
            minUnitHeight
          );
        }
      }
    );
  });

  public get hasResponsiveElements() {
    return Object.keys(this._responsiveWidgets).length > 0;
  }

  private _onResize = debounce(
    () => this.onResizeCallback(this.getResponsiveHeightLayout),
    50
  );
}

export const ResponsiveHeightContext = React.createContext<ResponsiveHeights>(
  new ResponsiveHeights(() => ({}))
);

interface ResponsiveHeightContextProviderProps {
  edit: boolean;
  setLayouts: (fn: LayoutTransformer) => void;
  children: React.ReactNode;
}

const ResponsiveHeightContextProvider: React.FC<ResponsiveHeightContextProviderProps> = ({
  edit,
  setLayouts,
  children,
}) => {
  const context = React.useRef<ResponsiveHeights>(
    new ResponsiveHeights(setLayouts)
  );

  context.current.onResizeCallback = setLayouts;

  React.useEffect(() => {
    if (context.current.hasResponsiveElements) {
      if (edit) {
        // Set default heights for all widgets in Edit mode
        setLayouts(context.current.getMinHeightLayout);
      } else {
        setLayouts(context.current.getResponsiveHeightLayout);
      }
    }
  }, [edit]);

  React.useEffect(() => context.current.destroy, []);

  return (
    <ResponsiveHeightContext.Provider value={context.current}>
      {children}
    </ResponsiveHeightContext.Provider>
  );
};

export default ResponsiveHeightContextProvider;
